import React, { useState, useRef } from "react";
import { useApi } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Copy, X } from "lucide-react";
import { money } from "@/lib/format";

export default function Import() {
  const { upload } = useApi();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onPick = (f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith(".csv") && !lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      toast.error("Please upload a CSV or XLSX file.");
      return;
    }
    setFile(f);
  };

  const onSubmit = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await upload("/contacts/import", fd);
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted.length} leads. ${r.data.duplicates.length} duplicates skipped.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Lead Intake</p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Import Leads
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Upload a CSV or XLSX file. ClaimWerks will parse every column, detect duplicates across all batches,
          score each lead against viability rules, and place them into your CRM. Each lead becomes one
          contact profile with full audit trail.
        </p>
      </div>

      <Card className="p-6 border-slate-200">
        <div
          className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
          data-testid="import-dropzone"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            data-testid="import-file-input"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
          <FileSpreadsheet className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          {file ? (
            <>
              <div className="font-semibold text-slate-900">{file.name}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono">
                {(file.size / 1024).toFixed(1)} KB
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }}
                className="text-xs text-slate-500 underline mt-2"
                data-testid="import-clear-file-btn"
              >
                Choose a different file
              </button>
            </>
          ) : (
            <>
              <p className="font-semibold text-slate-900">Drop your CSV or XLSX here</p>
              <p className="text-xs text-slate-500 mt-1">or click to browse · 20MB max</p>
            </>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            disabled={!file || busy}
            onClick={onSubmit}
            data-testid="import-submit-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busy ? "Importing…" : (<><Upload className="h-4 w-4 mr-1.5" /> Import & score</>)}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-5 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Import result</p>
              <h3 className="font-display text-lg font-bold text-slate-900 mt-1">
                {result.total_parsed} rows parsed
              </h3>
            </div>
            <div className="flex gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="import-result-inserted">
                <CheckCircle2 className="h-3 w-3 mr-1" /> {result.inserted.length} inserted
              </Badge>
              <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="import-result-duplicates">
                <Copy className="h-3 w-3 mr-1" /> {result.duplicates.length} duplicates
              </Badge>
            </div>
          </div>

          {result.inserted.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Inserted</p>
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="cw-table w-full">
                  <thead>
                    <tr>
                      <th>Claimant</th>
                      <th>Qualification</th>
                      <th className="text-right">Score</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.inserted.map((r) => (
                      <tr key={r.id}>
                        <td className="font-semibold">{r.client_name}</td>
                        <td>
                          <Badge variant="outline" className={
                            r.qualification === "qualified" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                            r.qualification === "research" ? "bg-amber-100 text-amber-800 border-amber-200" :
                            "bg-slate-100 text-slate-500 border-slate-200"
                          }>{r.qualification}</Badge>
                        </td>
                        <td className="text-right font-mono">{r.qualification_score}</td>
                        <td>
                          <Button size="sm" variant="ghost" onClick={() => nav(`/contacts/${r.id}`)} data-testid={`import-view-${r.id}`}>
                            View →
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.duplicates.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Duplicates skipped</p>
              <div className="space-y-1">
                {result.duplicates.map((d, i) => (
                  <div key={`${d.case_number || "nc"}-${d.property_street || ""}-${i}`} className="text-sm text-slate-600 flex items-center gap-2">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                    <span className="font-semibold">{d.client_name}</span>
                    <span className="text-xs font-mono text-slate-500">{d.case_number}</span>
                    <span className="text-xs text-slate-500">{d.property_street}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setFile(null); setResult(null); }} data-testid="import-reset-btn">
              <X className="h-4 w-4 mr-1" /> Reset
            </Button>
            <Button onClick={() => nav("/leads")} data-testid="import-go-leads-btn" className="bg-slate-900 hover:bg-slate-800">
              View in Leads
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-5 border-slate-200 bg-slate-50">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">How scoring works</p>
        <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
          <li>Surplus ≥ $10,000 and net (after 35% fee) ≥ $1,500 → +30 pts</li>
          <li>Entity/Trust/Estate owner → -5 pts (heir research flagged)</li>
          <li>Reachable phone (not DNC) → +10 pts</li>
          <li>Has email → +5 pts</li>
          <li>Deadline expired → auto-disqualify</li>
          <li>Deadline &lt; 30 days → +15 pts (urgent)</li>
        </ul>
        <p className="text-xs text-slate-500 mt-3">Score ≥ 70 → Qualified · 40–69 → Needs Research · &lt; 40 → Disqualified</p>
      </Card>
    </div>
  );
}
